/**
 * AKTools (AKShare HTTP) Provider
 * - Uses Tauri-side proxy HTTP (no CORS) when running in Tauri
 * - Uses Vite dev proxy (/aktools) in browser dev
 *
 * Focus: CN/HK stock + forex + CN index spot/hist where feasible.
 */
import {
  ApiProvider,
  Asset,
  AssetPrice,
  AssetType,
  TimeRange,
  HistoricalData,
  AssetSearchResult,
  AssetValidationResult,
  RequestConfig
} from '@/types/api';

import { aktoolsService } from '../aktools';
import { createApiError, categorizeHttpError, extractRetryAfter } from '../errors';
import { assetDictionaryCache } from '../assetDictionaryCache';

type AnyRow = Record<string, any>;

const HK_LIST_ENDPOINTS = [
  '/api/public/stock_hk_spot_em',
  '/api/public/stock_hk_spot',
  '/api/public/stock_hk_spot_sina'
];
const A_LIST_ENDPOINTS = [
  '/api/public/stock_zh_a_spot_em',
  '/api/public/stock_zh_a_spot',
  '/api/public/stock_zh_a_spot_sina'
];
const INVESTING_ENDPOINTS: Partial<Record<AssetType, string>> = {
  index: '/api/public/investing_index',
  stock: '/api/public/investing_stock_global',
  futures: '/api/public/investing_futures',
  forex: '/api/public/investing_fx',
  etf: '/api/public/investing_etf',
  bond: '/api/public/investing_bond',
  fund: '/api/public/investing_fund',
  crypto: '/api/public/investing_crypto'
};

function toIsoNow(): string {
  return new Date().toISOString();
}

const AKTOOLS_MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const INVESTING_MAX_STALE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (covers weekends/holidays)

function parseAktoolsDateTime(input: string): Date | null {
  const s = String(input || '').trim();
  if (!s) return null;
  // Normalize "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
  const normalized = s.includes(' ') && !s.includes('T') ? s.replace(' ', 'T') : s;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

function parseInvestingDateTime(input: string): Date | null {
  const raw = String(input || '').trim();
  if (!raw) return null;
  // Supports "MM/DD/YYYY" or "MM/DD/YYYY HH:mm:ss"
  if (raw.includes('/')) {
    const [datePart, timePart] = raw.split(' ');
    const [mm, dd, yyyy] = datePart.split('/');
    if (mm && dd && yyyy) {
      const normalized = `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}${timePart ? `T${timePart}` : ''}`;
      const d = new Date(normalized);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  // Fallback: "YYYY-MM-DD" or "YYYY-MM-DD HH:mm:ss"
  const normalized = raw.includes(' ') && !raw.includes('T') ? raw.replace(' ', 'T') : raw;
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d;
}

function parseNumberValue(input: unknown, context?: string): number {
  if (typeof input === 'number') return input;
  if (typeof input === 'string') {
    const cleaned = input.replace(/[,％%]/g, '').trim();
    if (!cleaned) return NaN;
    const n = Number(cleaned);
    if (Number.isFinite(n)) return n;
  }
  if (input !== null && input !== undefined && String(input).trim() !== '') {
    const ctx = context ? ` (${context})` : '';
    console.warn(`[aktools] failed to parse number${ctx}:`, input);
  }
  return NaN;
}

function ensureNotStale(
  lastUpdated: string | null | undefined,
  context: string,
  maxAgeMs: number = AKTOOLS_MAX_STALE_MS
): void {
  if (!lastUpdated) return;
  const d = parseAktoolsDateTime(lastUpdated);
  if (!d) return;
  const age = Date.now() - d.getTime();
  if (age > maxAgeMs) {
    throw createApiError(
      'VALIDATION_ERROR',
      `AKTools returned stale data for ${context} (last_updated=${lastUpdated})`,
      { provider: 'aktools', originalError: lastUpdated }
    );
  }
}

function toMs(dateLike: string | Date): number {
  const d = typeof dateLike === 'string' ? new Date(dateLike) : dateLike;
  return d.getTime();
}

function timeRangeDays(range: TimeRange): number {
  switch (range) {
    case '1D':
      return 1;
    case '7D':
      return 7;
    case '30D':
      return 30;
    case '90D':
      return 90;
    case '1Y':
      return 365;
  }
}

function formatYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

function formatYmdDash(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const CHART_POINTS_LIMIT = 120;

/** Compute from_date and to_date for 120 data points by interval (D/W/M). */
function dateRangeFor120Points(interval: 'D' | 'W' | 'M'): { start: string; end: string } {
  const now = new Date();
  const end = formatYmdDash(now);
  let startDate: Date;
  if (interval === 'D') {
    startDate = new Date(now.getTime() - (CHART_POINTS_LIMIT + 5) * 24 * 60 * 60 * 1000);
  } else if (interval === 'W') {
    startDate = new Date(now.getTime() - (CHART_POINTS_LIMIT * 7 + 14) * 24 * 60 * 60 * 1000);
  } else {
    startDate = new Date(now.getFullYear(), now.getMonth() - CHART_POINTS_LIMIT - 1, now.getDate());
  }
  const start = formatYmdDash(startDate);
  return { start, end };
}

function normalizeHkCode(symbol: string): string | null {
  const s = symbol.trim().toUpperCase();
  const m = s.match(/^(\d{1,5})(?:\.HK)?$/);
  if (!m) return null;
  return m[1].padStart(5, '0');
}

function normalizeACode(symbol: string): { code: string; exchange?: 'SH' | 'SZ' | 'BJ' } | null {
  const s = symbol.trim().toUpperCase();
  const m = s.match(/^(\d{6})(?:\.(SH|SZ|BJ))?$/);
  if (!m) return null;
  return { code: m[1], exchange: (m[2] as any) || undefined };
}

const COMMON_CURRENCY_CODES = new Set([
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'CNH', 'CHF', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD', 'KRW', 'INR',
  'BRL', 'MXN', 'RUB', 'ZAR', 'TRY', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'ILS', 'THB', 'IDR'
]);

function normalizeForexCode(symbol: string): { code: string; from: string; to: string; candidates: string[] } | null {
  const s = symbol.trim().toUpperCase();
  let from = '';
  let to = '';
  if (s.includes('/')) {
    const parts = s.split('/');
    from = (parts[0] ?? '').trim();
    to = (parts[1] ?? '').trim();
  } else if (s.includes('_')) {
    const parts = s.split('_');
    from = (parts[0] ?? '').trim();
    to = (parts[1] ?? '').trim();
  } else if (/^[A-Z]{6}$/.test(s)) {
    // Only treat as forex if both halves are valid currency codes
    const a = s.slice(0, 3);
    const b = s.slice(3);
    if (!COMMON_CURRENCY_CODES.has(a) || !COMMON_CURRENCY_CODES.has(b)) return null;
    from = a;
    to = b;
  } else {
    return null;
  }
  if (!/^[A-Z]{3}$/.test(from) || !/^[A-Z]{3}$/.test(to)) return null;

  const base = `${from}${to}`;
  const candidates: string[] = [base];

  // Some EM spot codes include a trailing "C" for middle price (e.g. EURCNYC)
  candidates.push(`${base}C`);

  // Practical fallback: treat CNY as CNH if needed
  if (to === 'CNY') {
    candidates.push(`${from}CNH`);
    candidates.push(`${from}CNHC`);
  }

  return { code: base, from, to, candidates };
}

function buildInvestingSymbolCandidates(symbol: string, type: AssetType): string[] {
  const raw = symbol.trim().toUpperCase();
  const candidates: string[] = [];
  const push = (value: string | null | undefined) => {
    const v = String(value || '').trim().toUpperCase();
    if (!v || candidates.includes(v)) return;
    candidates.push(v);
  };

  if (type === 'forex') {
    const fx = normalizeForexCode(raw);
    if (fx) {
      push(fx.code);
      for (const c of fx.candidates) push(c);
    }
  }

  const base = raw.split('.')[0] || raw;
  const cleaned = base.replace(/[^A-Z0-9]/g, '');
  push(cleaned);
  push(base);
  push(raw);

  return candidates;
}

function normalizeCnIndexSymbol(symbol: string): { code: string; candidates: string[] } | null {
  const s = symbol.trim().toLowerCase();
  if (/^(sh|sz)\d{6}$/.test(s)) return { code: s, candidates: [s] };
  if (/^\d{6}$/.test(s)) {
    // Try sh first, then sz
    return { code: `sh${s}`, candidates: [`sh${s}`, `sz${s}`] };
  }
  return null;
}

function extractCodeName(row: AnyRow): { code: string; name: string } {
  const codeCandidates = [
    row?.['代码'],
    row?.['code'],
    row?.['symbol'],
    row?.['股票代码'],
    row?.['证券代码']
  ];
  const nameCandidates = [
    row?.['名称'],
    row?.['name'],
    row?.['股票名称'],
    row?.['证券简称'],
    row?.['证券名称']
  ];

  const code = String(codeCandidates.find((v) => v !== undefined && v !== null) ?? '').trim();
  const name = String(nameCandidates.find((v) => v !== undefined && v !== null) ?? '').trim();
  return { code, name };
}

async function fetchListWithFallback(paths: string[], label: string, config?: RequestConfig): Promise<AnyRow[]> {
  let lastError: unknown = null;
  for (const path of paths) {
    try {
      const { data } = await aktoolsService.getJson<AnyRow[]>(
        path,
        undefined,
        { ...config, timeout: Math.min(config?.timeout ?? 30000, 45000) }
      );
      if (Array.isArray(data) && data.length > 0) {
        console.log(`[aktools] ${label} list from ${path} (${data.length} rows)`);
        return data;
      }
      console.warn(`[aktools] ${label} list empty from ${path}`);
    } catch (error) {
      lastError = error;
      const msg = error instanceof Error ? error.message : String(error);
      console.warn(`[aktools] ${label} list fetch failed from ${path}: ${msg}`);
    }
  }
  if (lastError) throw lastError;
  return [];
}

/**
 * Calculate relevance score for a search result
 * Similar to assetDataService.calculateRelevance but adapted for aktoolsProvider
 */
function calculateRelevanceForResult(query: string, name: string, symbol: string): number {
  const lowerQuery = query.toLowerCase().trim();
  const upperQuery = query.toUpperCase().trim();
  const lowerSymbol = symbol.toLowerCase();
  const upperSymbol = symbol.toUpperCase();
  const lowerName = name.toLowerCase();
  const upperName = name.toUpperCase();
  
  // Exact symbol match: 100 points
  if (lowerSymbol === lowerQuery) return 100;
  
  // Symbol starts with query: 80 points
  if (lowerSymbol.startsWith(lowerQuery)) return 80;
  
  // Symbol contains query (case-insensitive): 60 points
  // Use uppercase to ensure exact character matching (e.g., "BNB" vs "BNK")
  if (upperSymbol.includes(upperQuery)) return 60;
  
  // Exact name match: 50 points
  if (lowerName === lowerQuery) return 50;
  
  // Name starts with query: 40 points
  if (lowerName.startsWith(lowerQuery)) return 40;
  
  // Name contains query (case-insensitive, uppercase for exact character matching): 30 points
  // Use uppercase to avoid false matches like "bnb" matching "bnk" in "SHACOMBNK"
  if (upperName.includes(upperQuery)) return 30;
  
  // Word boundary match in name: 20 points
  const nameWords = lowerName.split(/[\s\-_]+/);
  if (nameWords.some(word => word.startsWith(lowerQuery))) return 20;
  
  // Partial word match (query is substring of a word): 10 points
  // Use uppercase to ensure exact character matching
  const upperNameWords = upperName.split(/[\s\-_]+/);
  if (upperNameWords.some(word => word.includes(upperQuery) && word.length > upperQuery.length)) return 10;
  
  return 0;
}

function isBondName(name: string): boolean {
  const n = String(name || '').trim();
  if (!n) return false;

  const upper = n.toUpperCase();
  const bondKeywords = [
    '债', '债券', '债务', '中期票据', '公司债', '企业债', '国债', '可转债', '可转换债',
    '金融债', '次级债', '永续债', '超短期融资券', '短期融资券', '定向工具', '资产支持证券',
    'MTN', 'BOND', 'ABS', 'MBS'
  ];

  if (bondKeywords.some((keyword) => (keyword.length === 1 ? n.includes(keyword) : upper.includes(keyword)))) {
    return true;
  }

  return /\bBT\b/.test(upper);
}

export class AktoolsProvider implements ApiProvider {
  name = 'aktools';
  priority = 0; // Prefer self-hosted provider when available
  isHealthy = true;
  lastError?: Date;
  supportedAssetTypes: AssetType[] = ['crypto', 'stock', 'forex', 'bond', 'index', 'futures', 'etf', 'fund'];

  private readonly timeout = 30000;
  private readonly SEARCH_LIST_TTL_MS = 10 * 60 * 1000; // 10 minutes
  private hkSpotCache: { fetchedAt: number; rows: Array<{ code: string; name: string }> } | null = null;
  private aSpotCache: { fetchedAt: number; rows: Array<{ code: string; name: string }> } | null = null;
  
  // Name index for fast search: Map<normalized_name, AssetSearchResult[]>
  private hkNameIndex: Map<string, AssetSearchResult[]> | null = null;
  private aNameIndex: Map<string, AssetSearchResult[]> | null = null;

  /**
   * Build name index for fast search
   * Indexes by normalized name (lowercase) and all substrings for prefix matching
   */
  private buildNameIndex(
    rows: Array<{ code: string; name: string }>,
    type: 'hk' | 'a'
  ): Map<string, AssetSearchResult[]> {
    const index = new Map<string, AssetSearchResult[]>();
    
    for (const r of rows) {
      if (!r.name || !r.code) continue;
      
      const nameLower = r.name.toLowerCase();
      const code = type === 'hk' ? r.code.padStart(5, '0') : r.code;
      const symbol = type === 'hk' ? `${code}.HK` : (() => {
        const ex = this.inferAExchange(code);
        return ex ? `${code}.${ex}` : code;
      })();
      const assetType = isBondName(r.name) ? 'bond' : 'stock';
      
      const result: AssetSearchResult = {
        id: `${assetType}-${symbol.toLowerCase()}`,
        symbol,
        name: r.name,
        type: assetType,
        ...(type === 'hk' ? { exchange: 'HK' } : (() => {
          const ex = this.inferAExchange(code);
          return ex ? { exchange: ex } : {};
        })())
      };
      
      // Index by full normalized name
      if (!index.has(nameLower)) {
        index.set(nameLower, []);
      }
      index.get(nameLower)!.push(result);
      
      // Index by words (for multi-word names)
      const words = nameLower.split(/[\s\-_]+/).filter(w => w.length >= 2);
      for (const word of words) {
        if (!index.has(word)) {
          index.set(word, []);
        }
        const wordResults = index.get(word)!;
        if (!wordResults.some(r => r.symbol === result.symbol)) {
          wordResults.push(result);
        }
      }
      
      // Index by prefixes (for fast prefix matching)
      // Only create 3+ character prefixes to avoid overly broad matches
      // This prevents "xiaomi" from matching "xi" prefix keys
      for (let i = 3; i <= Math.min(nameLower.length, 10); i++) {
        const prefix = nameLower.substring(0, i);
        if (!index.has(prefix)) {
          index.set(prefix, []);
        }
        const prefixResults = index.get(prefix)!;
        if (!prefixResults.some(r => r.symbol === result.symbol)) {
          prefixResults.push(result);
        }
      }
      
      // Also index by word prefixes (more precise for multi-word names)
      // This helps with names like "XIAOMI BT N3004" where we want to match "xiaomi" as a word
      for (const word of words) {
        if (word.length >= 3) {
          for (let i = 3; i <= Math.min(word.length, 10); i++) {
            const wordPrefix = word.substring(0, i);
            if (!index.has(wordPrefix)) {
              index.set(wordPrefix, []);
            }
            const wordPrefixResults = index.get(wordPrefix)!;
            if (!wordPrefixResults.some(r => r.symbol === result.symbol)) {
              wordPrefixResults.push(result);
            }
          }
        }
      }
    }
    
    return index;
  }

  private async fetchInvestingQuote(asset: Asset, type: AssetType, config?: RequestConfig): Promise<AssetPrice | null> {
    const endpoint = INVESTING_ENDPOINTS[type];
    if (!endpoint) return null;

    const candidates = buildInvestingSymbolCandidates(asset.symbol, type);
    if (candidates.length === 0) return null;

    const timeout = Math.min(config?.timeout ?? this.timeout, 20000);

    for (const candidate of candidates) {
      try {
        const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
          endpoint,
          { symbols: candidate },
          { ...config, timeout }
        );

        if (!Array.isArray(data) || data.length === 0) continue;

        const row =
          data.find((r) => String(r?.['symbol'] ?? '').toUpperCase() === candidate) ||
          data[0];

        const lastUpdatedRaw =
          row?.['date'] ??
          row?.['时间'] ??
          row?.['日期'] ??
          row?.['更新时间'] ??
          row?.['last_updated'] ??
          null;

        if (lastUpdatedRaw) {
          ensureNotStale(
            String(lastUpdatedRaw),
            `investing_${type} ${asset.symbol}`,
            INVESTING_MAX_STALE_MS
          );
        }

        const currentPrice = parseNumberValue(
          row?.['lp'] ??
          row?.['last_price'] ??
          row?.['price'] ??
          row?.['close'] ??
          row?.['收盘'] ??
          row?.['last'] ??
          row?.['最新价'] ??
          0,
          `investing_${type} ${asset.symbol} current_price`
        );
        if (!Number.isFinite(currentPrice) || currentPrice <= 0) continue;

        const change = parseNumberValue(
          row?.['ch'] ?? row?.['change'] ?? row?.['涨跌额'] ?? 0,
          `investing_${type} ${asset.symbol} change`
        );
        const changePct = parseNumberValue(
          row?.['chp'] ?? row?.['change_percent'] ?? row?.['涨跌幅'] ?? 0,
          `investing_${type} ${asset.symbol} change_pct`
        );
        const name = String(row?.['name'] ?? row?.['名称'] ?? row?.['symbol'] ?? asset.name ?? asset.symbol);

        return {
          id: asset.id,
          symbol: asset.symbol.toUpperCase(),
          name,
          current_price: currentPrice,
          price_change_24h: Number.isFinite(change) ? change : 0,
          price_change_percentage_24h: Number.isFinite(changePct) ? changePct : 0,
          last_updated: parseAktoolsDateTime(String(lastUpdatedRaw || ''))?.toISOString() || toIsoNow(),
          source_url: sourceUrl
        };
      } catch {
        continue;
      }
    }

    return null;
  }

  private async fetchInvestingHistory(
    asset: Asset,
    type: AssetType,
    timeRange: TimeRange,
    config?: RequestConfig
  ): Promise<HistoricalData | null> {
    const endpoint = INVESTING_ENDPOINTS[type];
    if (!endpoint) return null;

    const candidates = buildInvestingSymbolCandidates(asset.symbol, type);
    if (candidates.length === 0) return null;

    const timeout = Math.min(config?.timeout ?? this.timeout, 20000);
    const requestedInterval = config?.interval;
    let start: string;
    let end: string;
    let interval: string;
    if (requestedInterval === 'D' || requestedInterval === 'W' || requestedInterval === 'M') {
      const range = dateRangeFor120Points(requestedInterval);
      start = range.start;
      end = range.end;
      interval = requestedInterval;
    } else {
      const days = timeRangeDays(timeRange);
      const now = new Date();
      end = formatYmdDash(now);
      const startDate = new Date(now.getTime() - (days + 5) * 24 * 60 * 60 * 1000);
      start = formatYmdDash(startDate);
      interval = timeRange === '1D' ? '60' : timeRange === '1Y' ? 'W' : 'D';
    }

    for (const candidate of candidates) {
      try {
        const { data: searchData } = await aktoolsService.getJson<AnyRow[]>(
          endpoint,
          { query: candidate, limit: 5 },
          { ...config, timeout }
        );

        if (!Array.isArray(searchData) || searchData.length === 0) continue;

        const targetSymbol = (asset.symbol || '').trim().toUpperCase();
        const rowsWithTicker = searchData
          .map((r) => {
            const ticker =
              r?.['ticker'] ?? r?.['investing_id'] ?? r?.['investingId'] ?? r?.['id'] ?? null;
            const sym = String(r?.['symbol'] ?? '').trim();
            const name = String(r?.['name'] ?? r?.['full_name'] ?? r?.['description'] ?? '').trim();
            return { row: r, ticker, sym, name };
          })
          .filter((x) => x.ticker != null && String(x.ticker).trim() !== '');
        if (rowsWithTicker.length === 0) {
          console.warn(`[aktools] fetchInvestingHistory: search returned no ticker for ${type} query=${candidate}`, {
            symbol: asset.symbol,
            endpoint
          });
          continue;
        }
        const exactMatch = rowsWithTicker.find(
          (x) => x.sym.trim().toUpperCase() === targetSymbol
        );
        const firstRow = rowsWithTicker[0];
        const selected = exactMatch || firstRow;
        const investingId = String(selected!.ticker).trim();

        const histParams = { investing_id: investingId, from_date: start, to_date: end, interval };

        const { data: histData } = await aktoolsService.getJson<AnyRow[]>(
          endpoint,
          histParams,
          { ...config, timeout: Math.min(timeout, 30000) }
        );

        if (!Array.isArray(histData) || histData.length === 0) {
          console.warn(`[aktools] fetchInvestingHistory: empty history for ${type} investing_id=${investingId}`, {
            symbol: asset.symbol,
            endpoint
          });
          continue;
        }

        const points = histData
          .map((r) => {
            const dt = parseInvestingDateTime(String(r?.['date'] ?? r?.['日期'] ?? r?.['time'] ?? ''));
            const t = dt ? dt.getTime() : NaN;
            const p = parseNumberValue(
              r?.['close'] ?? r?.['收盘'] ?? r?.['price'] ?? r?.['last'] ?? r?.['lp'] ?? 0,
              `investing_${type} ${asset.symbol} hist_close`
            );
            const v = parseNumberValue(
              r?.['volume'] ?? r?.['成交量'] ?? 0,
              `investing_${type} ${asset.symbol} hist_volume`
            );
            return { t, p, v };
          })
          .filter((x) => Number.isFinite(x.t) && Number.isFinite(x.p) && x.p > 0)
          .sort((a, b) => a.t - b.t);

        if (points.length === 0) {
          console.warn(`[aktools] fetchInvestingHistory: no valid price points after parse for ${type} investing_id=${investingId}`, {
            symbol: asset.symbol,
            rowCount: histData.length,
            endpoint
          });
          continue;
        }

        const sliced = points.length > CHART_POINTS_LIMIT ? points.slice(-CHART_POINTS_LIMIT) : points;
        return {
          asset,
          timeRange,
          data: {
            prices: sliced.map((x) => ({ timestamp: x.t, value: x.p })),
            volumes: sliced.map((x) => ({ timestamp: x.t, value: Number.isFinite(x.v) ? x.v : 0 }))
          },
          metadata: {
            fetchedAt: new Date(),
            source: 'AKTools',
            interval
          }
        };
      } catch {
        continue;
      }
    }

    return null;
  }

  private async getHkSpotList(config?: RequestConfig): Promise<Array<{ code: string; name: string }>> {
    const now = Date.now();
    if (this.hkSpotCache && now - this.hkSpotCache.fetchedAt < this.SEARCH_LIST_TTL_MS) {
      return this.hkSpotCache.rows;
    }
    
    // Priority 1: Try built-in dictionary (fastest, no network)
    try {
      const cacheResult = await assetDictionaryCache.loadCache();
      if (cacheResult.assets.length > 0) {
        // Filter HK stocks from cache
        const hkAssets = cacheResult.assets.filter(a => a.exchange === 'HK' && a.type === 'stock');
        if (hkAssets.length > 0) {
          // Convert AssetSearchResult[] to { code, name }[]
          const rows = hkAssets.map(a => {
            // Extract code from symbol like "0700.HK" -> "00700" (5 digits, padded)
            const codeWithoutSuffix = a.symbol.replace(/\.HK$/i, '');
            const code = normalizeHkCode(codeWithoutSuffix) || codeWithoutSuffix.padStart(5, '0');
            return { code, name: a.name };
          }).filter(r => r.code && r.name);
          
          if (rows.length > 0) {
            console.log(`[AktoolsProvider] Using ${cacheResult.source} dictionary for HK stocks: ${rows.length} items`);
            this.hkSpotCache = { fetchedAt: now, rows };
            this.hkNameIndex = this.buildNameIndex(rows, 'hk');
            return rows;
          }
        }
      }
    } catch (error) {
      console.warn('[AktoolsProvider] Failed to load dictionary cache, falling back to network:', error);
    }
    
    // Priority 2: Fallback to network (only if dictionary is unavailable)
    const data = await fetchListWithFallback(HK_LIST_ENDPOINTS, 'HK', config);
    const rows = (Array.isArray(data) ? data : [])
      .map((r) => {
        const { code, name } = extractCodeName(r);
        return { code, name };
      })
      .filter((r) => r.code && r.name);
    this.hkSpotCache = { fetchedAt: now, rows };
    
    // Rebuild index when cache is updated
    this.hkNameIndex = this.buildNameIndex(rows, 'hk');
    
    return rows;
  }

  private async getASpotList(config?: RequestConfig): Promise<Array<{ code: string; name: string }>> {
    const now = Date.now();
    if (this.aSpotCache && now - this.aSpotCache.fetchedAt < this.SEARCH_LIST_TTL_MS) {
      return this.aSpotCache.rows;
    }
    
    // Priority 1: Try built-in dictionary (fastest, no network)
    try {
      const cacheResult = await assetDictionaryCache.loadCache();
      if (cacheResult.assets.length > 0) {
        // Filter A-share stocks from cache
        const aAssets = cacheResult.assets.filter(a => 
          (a.exchange === 'SH' || a.exchange === 'SZ' || a.exchange === 'BJ') && a.type === 'stock'
        );
        if (aAssets.length > 0) {
          // Convert AssetSearchResult[] to { code, name }[]
          const rows = aAssets.map(a => {
            // Extract code from symbol like "600519.SH" -> "600519"
            const code = a.symbol.replace(/\.(SH|SZ|BJ)$/i, '');
            return { code, name: a.name };
          }).filter(r => r.code && r.name);
          
          if (rows.length > 0) {
            console.log(`[AktoolsProvider] Using ${cacheResult.source} dictionary for A-share stocks: ${rows.length} items`);
            this.aSpotCache = { fetchedAt: now, rows };
            this.aNameIndex = this.buildNameIndex(rows, 'a');
            return rows;
          }
        }
      }
    } catch (error) {
      console.warn('[AktoolsProvider] Failed to load dictionary cache, falling back to network:', error);
    }
    
    // Priority 2: Fallback to network (only if dictionary is unavailable)
    const data = await fetchListWithFallback(A_LIST_ENDPOINTS, 'A-share', config);
    const rows = (Array.isArray(data) ? data : [])
      .map((r) => {
        const { code, name } = extractCodeName(r);
        return { code, name };
      })
      .filter((r) => r.code && r.name);
    this.aSpotCache = { fetchedAt: now, rows };
    
    // Rebuild index when cache is updated
    this.aNameIndex = this.buildNameIndex(rows, 'a');
    
    return rows;
  }

  private inferAExchange(code: string): 'SH' | 'SZ' | 'BJ' | undefined {
    const c = String(code || '').trim();
    if (/^6\d{5}$/.test(c)) return 'SH';
    if (/^(0|3)\d{5}$/.test(c)) return 'SZ';
    if (/^(4|8)\d{5}$/.test(c)) return 'BJ';
    return undefined;
  }

  async getAssetPrice(asset: Asset, config?: RequestConfig): Promise<AssetPrice> {
    try {
      const timeout = config?.timeout ?? this.timeout;

      switch (asset.type) {
        case 'stock': {
          // HK stock: 0700.HK -> 00700 — prefer investing first, then EM/min endpoints
          const hkCode = normalizeHkCode(asset.symbol);
          const isHk = hkCode !== null || asset.exchange?.toUpperCase() === 'HK' || asset.symbol.toUpperCase().endsWith('.HK');
          if (isHk) {
            const investingHk = await this.fetchInvestingQuote(asset, 'stock', config);
            if (investingHk) return investingHk;

            try {
              const code = hkCode ?? normalizeHkCode(asset.symbol.replace(/\.HK$/i, ''));
              if (!code) {
                throw createApiError('VALIDATION_ERROR', `Invalid HK stock symbol: ${asset.symbol}`, { provider: this.name });
              }

              // Fallback: spot endpoint (has change fields), but it can be unstable.
              try {
                const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
                  '/api/public/stock_hk_spot_em',
                  undefined,
                  { ...config, timeout }
                );
                const row = data.find((r) => String(r['代码']).toUpperCase() === code);
                if (row) {
                  const lastUpdated = toIsoNow();
                  return {
                    id: asset.id,
                    symbol: asset.symbol.toUpperCase(),
                    name: String(row['名称'] || asset.name || asset.symbol),
                    current_price: Number(row['最新价'] ?? 0),
                    price_change_24h: Number(row['涨跌额'] ?? 0),
                    price_change_percentage_24h: Number(row['涨跌幅'] ?? 0),
                    last_updated: lastUpdated,
                    source_url: sourceUrl
                  };
                }
              } catch (e: any) {
                const codeStr = e?.code;
                const status = e?.response?.status;
                if (!(codeStr || status)) {
                  // ignore; fallback below
                }
              }

              // Fallback: minute data (stable, but may not include change fields)
              const { data: minData, sourceUrl: minUrl } = await aktoolsService.getJson<AnyRow[]>(
                '/api/public/stock_hk_hist_min_em',
                { symbol: code },
                { ...config, timeout }
              );
              if (!Array.isArray(minData) || minData.length === 0) {
                throw createApiError('NOT_FOUND_ERROR', `HK stock not found: ${asset.symbol}`, { provider: this.name });
              }
              const last = minData[minData.length - 1] as AnyRow;
              const price = Number(last['最新价'] ?? last['收盘'] ?? 0);
              const lastUpdatedRaw = last['时间'] ? String(last['时间']) : null;
              ensureNotStale(lastUpdatedRaw, `stock(HK) ${asset.symbol}`);
              return {
                id: asset.id,
                symbol: asset.symbol.toUpperCase(),
                name: asset.name || `${code}.HK`,
                current_price: price,
                price_change_24h: 0,
                price_change_percentage_24h: 0,
                last_updated: parseAktoolsDateTime(lastUpdatedRaw || '')?.toISOString() || toIsoNow(),
                source_url: minUrl
              };
            } catch (error) {
              throw error;
            }
          }

          // A-share: 600519.SH / 600519 — prefer investing first, then EM/hist endpoints
          const a = normalizeACode(asset.symbol);
          const exchange = asset.exchange?.toUpperCase();
          if (a || exchange === 'SH' || exchange === 'SZ' || exchange === 'BJ') {
            const investingA = await this.fetchInvestingQuote(asset, 'stock', config);
            if (investingA) return investingA;

            try {
              const code = a?.code || asset.symbol.replace(/\.(SH|SZ|BJ)$/i, '');

              // Fallback: minute data (stable and includes change fields).
              try {
                const { data: minData, sourceUrl: minUrl } = await aktoolsService.getJson<AnyRow[]>(
                  '/api/public/stock_zh_a_hist_min_em',
                  { symbol: code },
                  { ...config, timeout }
                );
                if (Array.isArray(minData) && minData.length > 0) {
                  const last = minData[minData.length - 1] as AnyRow;
                  const price = Number(last['收盘'] ?? 0);
                  const lastUpdatedRaw = last['时间'] ? String(last['时间']) : null;
                  ensureNotStale(lastUpdatedRaw, `stock(CN) ${asset.symbol}`);
                  return {
                    id: asset.id,
                    symbol: asset.symbol.toUpperCase(),
                    name: asset.name || asset.symbol,
                    current_price: price,
                    price_change_24h: Number(last['涨跌额'] ?? 0),
                    price_change_percentage_24h: Number(last['涨跌幅'] ?? 0),
                    last_updated: parseAktoolsDateTime(lastUpdatedRaw || '')?.toISOString() || toIsoNow(),
                    source_url: minUrl
                  };
                }
              } catch {
                // fall through
              }

              // Fallback: daily hist over a short range
              const now = new Date();
              const end = formatYmd(now);
              const startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
              const start = formatYmd(startDate);
              const { data: histData, sourceUrl: histUrl } = await aktoolsService.getJson<AnyRow[]>(
                '/api/public/stock_zh_a_hist',
                { symbol: code, period: 'daily', start_date: start, end_date: end, adjust: '' },
                { ...config, timeout }
              );
              if (!Array.isArray(histData) || histData.length === 0) {
                throw createApiError('NOT_FOUND_ERROR', `A-share stock not found: ${asset.symbol}`, { provider: this.name });
              }
              const last = histData[histData.length - 1] as AnyRow;
              const lastUpdatedRaw = last['日期'] ? String(last['日期']) : null;
              ensureNotStale(lastUpdatedRaw, `stock(CN) ${asset.symbol}`);
              return {
                id: asset.id,
                symbol: asset.symbol.toUpperCase(),
                name: asset.name || asset.symbol,
                current_price: Number(last['收盘'] ?? 0),
                price_change_24h: Number(last['涨跌额'] ?? 0),
                price_change_percentage_24h: Number(last['涨跌幅'] ?? 0),
                last_updated: parseAktoolsDateTime(lastUpdatedRaw || '')?.toISOString() || toIsoNow(),
                source_url: histUrl
              };
            } catch (error) {
              throw error;
            }
          }

          const investing = await this.fetchInvestingQuote(asset, 'stock', config);
          if (investing) return investing;

          throw createApiError('VALIDATION_ERROR', `Unsupported stock symbol for AKTools: ${asset.symbol}`, {
            provider: this.name
          });
        }

        case 'forex': {
          const fx = normalizeForexCode(asset.symbol);
          if (!fx) {
            throw createApiError('VALIDATION_ERROR', `Invalid forex symbol: ${asset.symbol}`, { provider: this.name });
          }
          // 汇率仅用 investing_fx（如 /api/public/investing_fx?symbols=USDCNY），不用 forex_spot_em / forex_hist_em
          const investing = await this.fetchInvestingQuote(asset, 'forex', config);
          if (investing) return investing;
          throw createApiError('NOT_FOUND_ERROR', `Forex pair not found: ${asset.symbol}`, { provider: this.name });
        }

        case 'index': {
          const investing = await this.fetchInvestingQuote(asset, 'index', config);
          if (investing) return investing;
          throw createApiError('NOT_FOUND_ERROR', `Index not found: ${asset.symbol}`, { provider: this.name });
        }

        case 'crypto': {
          // This server exposes a small list (10 rows) from crypto_js_spot.
          // We match by trading pair prefix (e.g. BTC*, ETH*), prefer USD quotes if present.
          const sym = asset.symbol.trim().toUpperCase();
          const cleaned = sym.replace(/[^A-Z0-9]/g, '');
          if (!/^[A-Z]{2,10}$/.test(sym) && !/^[A-Z]{2,10}$/.test(cleaned)) {
            const investing = await this.fetchInvestingQuote(asset, 'crypto', config);
            if (investing) return investing;
            throw createApiError('VALIDATION_ERROR', `Unsupported crypto symbol for AKTools: ${asset.symbol}`, {
              provider: this.name
            });
          }

          try {
            const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
              '/api/public/crypto_js_spot',
              undefined,
              { ...config, timeout }
            );

            const matches = data.filter((r) => String(r['交易品种'] || '').toUpperCase().startsWith(sym));
            const row =
              matches.find((r) => String(r['交易品种']).toUpperCase().endsWith('USD')) ||
              matches[0] ||
              null;

            if (!row) {
              throw createApiError('NOT_FOUND_ERROR', `Crypto not found in AKTools spot list: ${asset.symbol}`, {
                provider: this.name
              });
            }

            const updated = row['更新时间'] ? String(row['更新时间']) : null;
            ensureNotStale(updated, `crypto ${asset.symbol}`);
            return {
              id: asset.id,
              symbol: asset.symbol.toUpperCase(),
              name: String(row['交易品种'] || asset.name || asset.symbol),
              current_price: Number(row['最近报价'] ?? 0),
              price_change_24h: Number(row['涨跌额'] ?? 0),
              price_change_percentage_24h: Number(row['涨跌幅'] ?? 0),
              last_updated: parseAktoolsDateTime(updated || '')?.toISOString() || toIsoNow(),
              source_url: sourceUrl
            };
          } catch (error) {
            const investing = await this.fetchInvestingQuote(asset, 'crypto', config);
            if (investing) return investing;
            throw error;
          }
        }

        case 'bond': {
          // Bond support: try bond_zh_hs_spot first, then bond_zh_hs_cov_spot (convertible bonds)
          // Symbol format: typically 6-digit code like stocks (e.g., 123456)
          const sym = asset.symbol.trim().toUpperCase();
          const bondCode = sym.replace(/\.(SH|SZ|BJ)$/i, '').trim();
          
          if (!/^\d{6}$/.test(bondCode)) {
            const investing = await this.fetchInvestingQuote(asset, 'bond', config);
            if (investing) return investing;
            throw createApiError('VALIDATION_ERROR', `Invalid bond symbol format: ${asset.symbol}`, {
              provider: this.name
            });
          }

          // Try regular bond spot first
          try {
            const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
              '/api/public/bond_zh_hs_spot',
              undefined,
              { ...config, timeout }
            );

            const row = data.find((r) => {
              const code = String(r['代码'] ?? '').trim();
              return code === bondCode || code === sym;
            });

            if (row) {
              const lastUpdated = toIsoNow();
              return {
                id: asset.id,
                symbol: asset.symbol.toUpperCase(),
                name: String(row['名称'] || row['债券简称'] || asset.name || asset.symbol),
                current_price: Number(row['最新价'] ?? row['收盘价'] ?? 0),
                price_change_24h: Number(row['涨跌额'] ?? 0),
                price_change_percentage_24h: Number(row['涨跌幅'] ?? 0),
                last_updated: lastUpdated,
                source_url: sourceUrl
              };
            }
          } catch {
            // Fall through to convertible bond endpoint
          }

          // Fallback: try convertible bond spot
          try {
            const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
              '/api/public/bond_zh_hs_cov_spot',
              undefined,
              { ...config, timeout }
            );

            const row = data.find((r) => {
              const code = String(r['代码'] ?? '').trim();
              return code === bondCode || code === sym;
            });

            if (row) {
              const lastUpdated = toIsoNow();
              return {
                id: asset.id,
                symbol: asset.symbol.toUpperCase(),
                name: String(row['名称'] || row['债券简称'] || asset.name || asset.symbol),
                current_price: Number(row['最新价'] ?? row['收盘价'] ?? 0),
                price_change_24h: Number(row['涨跌额'] ?? 0),
                price_change_percentage_24h: Number(row['涨跌幅'] ?? 0),
                last_updated: lastUpdated,
                source_url: sourceUrl
              };
            }
          } catch {
            // Fall through to daily history
          }

          // Final fallback: use daily history to get latest price
          const now = new Date();
          const end = formatYmd(now);
          const startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          const start = formatYmd(startDate);

          try {
            const { data: histData, sourceUrl: histUrl } = await aktoolsService.getJson<AnyRow[]>(
              '/api/public/bond_zh_hs_daily',
              { symbol: bondCode, start_date: start, end_date: end },
              { ...config, timeout }
            );

            if (Array.isArray(histData) && histData.length > 0) {
              const last = histData[histData.length - 1] as AnyRow;
              const lastUpdatedRaw = last['日期'] ? String(last['日期']) : null;
              ensureNotStale(lastUpdatedRaw, `bond ${asset.symbol}`);
              return {
                id: asset.id,
                symbol: asset.symbol.toUpperCase(),
                name: asset.name || asset.symbol,
                current_price: Number(last['收盘'] ?? last['收盘价'] ?? 0),
                price_change_24h: Number(last['涨跌额'] ?? 0),
                price_change_percentage_24h: Number(last['涨跌幅'] ?? 0),
                last_updated: parseAktoolsDateTime(lastUpdatedRaw || '')?.toISOString() || toIsoNow(),
                source_url: histUrl
              };
            }
          } catch {
            // Continue to error
          }

          throw createApiError('NOT_FOUND_ERROR', `Bond not found: ${asset.symbol}`, { provider: this.name });
        }

        case 'futures': {
          const investing = await this.fetchInvestingQuote(asset, 'futures', config);
          if (investing) return investing;
          throw createApiError('NOT_FOUND_ERROR', `Futures not found: ${asset.symbol}`, { provider: this.name });
        }

        case 'etf': {
          const investing = await this.fetchInvestingQuote(asset, 'etf', config);
          if (investing) return investing;
          throw createApiError('NOT_FOUND_ERROR', `ETF not found: ${asset.symbol}`, { provider: this.name });
        }

        case 'fund': {
          const investing = await this.fetchInvestingQuote(asset, 'fund', config);
          if (investing) return investing;
          throw createApiError('NOT_FOUND_ERROR', `Fund not found: ${asset.symbol}`, { provider: this.name });
        }
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAssetPrices(assets: Asset[], config?: RequestConfig): Promise<AssetPrice[]> {
    const results: AssetPrice[] = [];
    let fatalError: any = null;
    for (const asset of assets) {
      try {
        results.push(await this.getAssetPrice(asset, config));
      } catch (e) {
        console.warn(`[aktools] getAssetPrice failed for ${asset.symbol}:`, e);
        const err: any = e;
        const code = err && typeof err === 'object' ? err.code : undefined;
        const message = err && typeof err === 'object' ? String(err.message || '') : '';
        const isStale = message.toLowerCase().includes('stale data');
        if (isStale || code === 'NETWORK_ERROR' || code === 'TIMEOUT_ERROR' || code === 'RATE_LIMIT_ERROR') {
          fatalError = fatalError || e;
        }
      }
    }
    // If AKTools is returning stale / unavailable data for this batch, trigger failover.
    if (fatalError) {
      throw fatalError;
    }
    return results;
  }

  async getHistoricalData(asset: Asset, timeRange: TimeRange, config?: RequestConfig): Promise<HistoricalData> {
    try {
      const days = timeRangeDays(timeRange);
      const now = new Date();
      const end = formatYmd(now);
      const startDate = new Date(now.getTime() - (days + 5) * 24 * 60 * 60 * 1000); // include buffer for non-trading days
      const start = formatYmd(startDate);

      if (asset.type === 'forex') {
        const investingHist = await this.fetchInvestingHistory(asset, 'forex', timeRange, config);
        if (investingHist) return investingHist;
        throw createApiError('NOT_FOUND_ERROR', `Forex history not found: ${asset.symbol}`, {
          provider: this.name
        });
      }

      if (asset.type === 'stock') {
        const hkCode = normalizeHkCode(asset.symbol);
        if (hkCode || asset.exchange?.toUpperCase() === 'HK') {
          const code = hkCode || asset.symbol.replace(/\.HK$/i, '').padStart(5, '0');
          const { data, sourceUrl } = await aktoolsService.getJson<AnyRow[]>(
            '/api/public/stock_hk_daily',
            { symbol: code, adjust: '' },
            config
          );

          const points = data
            .map((r) => ({
              t: toMs(String(r['date'])),
              p: Number(r['close'] ?? 0),
              v: Number(r['volume'] ?? 0)
            }))
            .filter((x) => Number.isFinite(x.t) && Number.isFinite(x.p) && x.p > 0)
            .sort((a, b) => a.t - b.t);

          const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
          const sliced = points.filter((x) => x.t >= cutoff);

          return {
            asset,
            timeRange,
            data: {
              prices: sliced.map((x) => ({ timestamp: x.t, value: x.p })),
              volumes: sliced.map((x) => ({ timestamp: x.t, value: Number.isFinite(x.v) ? x.v : 0 }))
            },
            metadata: {
              fetchedAt: new Date(),
              source: this.name,
              interval: '1d'
            }
          };
        }

        const a = normalizeACode(asset.symbol);
        if (a || asset.exchange?.toUpperCase() === 'SH' || asset.exchange?.toUpperCase() === 'SZ') {
          const investingHist = await this.fetchInvestingHistory(asset, 'stock', timeRange, config);
          if (investingHist) return investingHist;
          throw createApiError('NOT_FOUND_ERROR', `A-share history not found: ${asset.symbol}`, {
            provider: this.name
          });
        }

        const investingHist = await this.fetchInvestingHistory(asset, 'stock', timeRange, config);
        if (investingHist) return investingHist;

        throw createApiError('NOT_FOUND_ERROR', `Historical data not supported for stock: ${asset.symbol}`, {
          provider: this.name
        });
      }

      if (asset.type === 'index') {
        const idx = normalizeCnIndexSymbol(asset.symbol);
        if (!idx) {
          const investingHist = await this.fetchInvestingHistory(asset, 'index', timeRange, config);
          if (investingHist) return investingHist;
          throw createApiError('NOT_FOUND_ERROR', `Historical data not supported for index: ${asset.symbol}`, {
            provider: this.name
          });
        }

        // Try sh first; if empty, try sz
        let series: AnyRow[] | null = null;
        let usedSymbol: string = idx.candidates[0] || idx.code;
        for (const candidate of idx.candidates) {
          const { data } = await aktoolsService.getJson<AnyRow[]>(
            '/api/public/stock_zh_index_daily_em',
            { symbol: candidate },
            config
          );
          if (Array.isArray(data) && data.length > 0) {
            series = data;
            usedSymbol = candidate;
            break;
          }
        }

        if (!series) {
          throw createApiError('NOT_FOUND_ERROR', `Index history not found: ${asset.symbol}`, { provider: this.name });
        }

        const points = series
          .map((r) => ({
            t: toMs(String(r['date'])),
            p: Number(r['close'] ?? 0),
            v: Number(r['volume'] ?? 0)
          }))
          .filter((x) => Number.isFinite(x.t) && Number.isFinite(x.p) && x.p > 0)
          .sort((a2, b2) => a2.t - b2.t);

        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        const sliced = points.filter((x) => x.t >= cutoff);

        return {
          asset: { ...asset, symbol: asset.symbol, name: asset.name || usedSymbol.toUpperCase() },
          timeRange,
          data: {
            prices: sliced.map((x) => ({ timestamp: x.t, value: x.p })),
            volumes: sliced.map((x) => ({ timestamp: x.t, value: Number.isFinite(x.v) ? x.v : 0 }))
          },
          metadata: {
            fetchedAt: new Date(),
            source: 'AKTools',
            interval: '1d'
          }
        };
      }

      if (asset.type === 'bond') {
        const sym = asset.symbol.trim().toUpperCase();
        const bondCode = sym.replace(/\.(SH|SZ|BJ)$/i, '').trim();
        
        if (!/^\d{6}$/.test(bondCode)) {
          throw createApiError('VALIDATION_ERROR', `Invalid bond symbol format: ${asset.symbol}`, {
            provider: this.name
          });
        }

        // Try regular bond daily first
        let series: AnyRow[] | null = null;
        try {
          const { data } = await aktoolsService.getJson<AnyRow[]>(
            '/api/public/bond_zh_hs_daily',
            { symbol: bondCode, start_date: start, end_date: end },
            config
          );
          if (Array.isArray(data) && data.length > 0) {
            series = data;
          }
        } catch {
          // Fall through to convertible bond
        }

        // Fallback: try convertible bond daily
        if (!series) {
          try {
            const { data } = await aktoolsService.getJson<AnyRow[]>(
              '/api/public/bond_zh_hs_cov_daily',
              { symbol: bondCode, start_date: start, end_date: end },
              config
            );
            if (Array.isArray(data) && data.length > 0) {
              series = data;
            }
          } catch {
            // Continue to error
          }
        }

        if (!series) {
          const investingHist = await this.fetchInvestingHistory(asset, 'bond', timeRange, config);
          if (investingHist) return investingHist;
          throw createApiError('NOT_FOUND_ERROR', `Bond history not found: ${asset.symbol}`, { provider: this.name });
        }

        const points = series
          .map((r) => ({
            t: toMs(String(r['日期'])),
            p: Number(r['收盘'] ?? r['收盘价'] ?? 0),
            v: Number(r['成交量'] ?? 0)
          }))
          .filter((x) => Number.isFinite(x.t) && Number.isFinite(x.p) && x.p > 0)
          .sort((a2, b2) => a2.t - b2.t);

        return {
          asset,
          timeRange,
          data: {
            prices: points.map((x) => ({ timestamp: x.t, value: x.p })),
            volumes: points.map((x) => ({ timestamp: x.t, value: Number.isFinite(x.v) ? x.v : 0 }))
          },
          metadata: {
            fetchedAt: new Date(),
            source: 'AKTools',
            interval: '1d'
          }
        };
      }

      if (asset.type === 'futures') {
        const investingHist = await this.fetchInvestingHistory(asset, 'futures', timeRange, config);
        if (investingHist) return investingHist;
        throw createApiError('NOT_FOUND_ERROR', `Futures history not found: ${asset.symbol}`, { provider: this.name });
      }

      if (asset.type === 'crypto') {
        const investingHist = await this.fetchInvestingHistory(asset, 'crypto', timeRange, config);
        if (investingHist) return investingHist;
        throw createApiError('NOT_FOUND_ERROR', `Crypto history not found: ${asset.symbol}`, { provider: this.name });
      }

      if (asset.type === 'etf') {
        const investingHist = await this.fetchInvestingHistory(asset, 'etf', timeRange, config);
        if (investingHist) return investingHist;
        throw createApiError('NOT_FOUND_ERROR', `ETF history not found: ${asset.symbol}`, { provider: this.name });
      }

      if (asset.type === 'fund') {
        const investingHist = await this.fetchInvestingHistory(asset, 'fund', timeRange, config);
        if (investingHist) return investingHist;
        throw createApiError('NOT_FOUND_ERROR', `Fund history not found: ${asset.symbol}`, { provider: this.name });
      }

      throw createApiError('NOT_FOUND_ERROR', `Historical data not supported by AKTools for ${asset.type}`, {
        provider: this.name
      });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async searchAssets(query: string, type?: AssetType, config?: RequestConfig): Promise<AssetSearchResult[]> {
    const raw = query.trim();
    const q = raw.toUpperCase();
    if (!q || q.length < 2) return [];

    const results: AssetSearchResult[] = [];
    const seen = new Set<string>();
    const add = (r: AssetSearchResult) => {
      const key = `${r.symbol.toUpperCase()}-${r.type}`;
      if (seen.has(key)) return;
      seen.add(key);
      results.push(r);
    };

    // 1) Fast symbol-pattern matches (no network)
    if (!type || type === 'stock') {
      const hk = normalizeHkCode(q);
      if (hk) add({ id: `stock-${hk}.hk`, symbol: `${hk}.HK`, name: `${hk}.HK`, type: 'stock', exchange: 'HK' });

      const a = normalizeACode(q);
      if (a) {
        const sym = a.exchange ? `${a.code}.${a.exchange}` : a.code;
        add({ id: `stock-${sym.toLowerCase()}`, symbol: sym, name: sym, type: 'stock', ...(a.exchange && { exchange: a.exchange }) });
      }
    }

    if (!type || type === 'forex') {
      const fx = normalizeForexCode(q);
      if (fx) add({ id: `forex-${fx.from.toLowerCase()}_${fx.to.toLowerCase()}`, symbol: `${fx.from}/${fx.to}`, name: `${fx.from}/${fx.to}`, type: 'forex' });
    }

    if (!type || type === 'index') {
      // Be conservative for indices: only accept explicit SH/SZ prefix to avoid classifying
      // ordinary 6-digit stock codes as indices.
      if (/^(sh|sz)\d{6}$/i.test(q)) {
        const idx = normalizeCnIndexSymbol(q);
        if (idx) add({ id: `index-${idx.code}`, symbol: idx.code.toUpperCase(), name: idx.code.toUpperCase(), type: 'index' });
      }
    }

    if (type === 'futures' || type === 'etf' || type === 'fund') {
      const sym = q.trim();
      if (sym.length >= 2) {
        add({ id: `${type}-${sym.toLowerCase()}`, symbol: sym, name: sym, type });
      }
    }

    // 2) Fast alias matching (no network, always execute for common companies)
    // This should run before dictionary search to ensure common aliases are always found
    const lowerQ = raw.toLowerCase();
    const hasHan = /[\u4e00-\u9fa5]/.test(raw);
    const looksLikeWord = /[a-z]/i.test(raw) || hasHan;

    if ((!type || type === 'stock') && looksLikeWord) {
      // English aliases for a few common CN/HK companies, to support inputs like "tenc"
      const alias = lowerQ;
      if (alias.startsWith('tenc') || alias.startsWith('tencent')) {
        add({ id: 'stock-0700.hk', symbol: '0700.HK', name: 'Tencent Holdings', type: 'stock', exchange: 'HK' });
      }
      if (alias.startsWith('xiaomi') || alias.startsWith('mi')) {
        add({ id: 'stock-1810.hk', symbol: '1810.HK', name: 'Xiaomi', type: 'stock', exchange: 'HK' });
      }
      if (alias.includes('moutai') || alias.includes('maotai')) {
        add({ id: 'stock-600519.sh', symbol: '600519.SH', name: 'Kweichow Moutai', type: 'stock', exchange: 'SH' });
      }
      // Chinese aliases (quick win even if list endpoints are slow/unavailable)
      if (raw.includes('腾讯')) {
        add({ id: 'stock-0700.hk', symbol: '0700.HK', name: '腾讯控股', type: 'stock', exchange: 'HK' });
      }
      if (raw.includes('小米')) {
        add({ id: 'stock-1810.hk', symbol: '1810.HK', name: '小米集团', type: 'stock', exchange: 'HK' });
      }
      if (raw.includes('茅台')) {
        add({ id: 'stock-600519.sh', symbol: '600519.SH', name: '贵州茅台', type: 'stock', exchange: 'SH' });
      }
    }

    // 3) Name-based HK/A dictionary search (network + cached, using index for fast lookup)
    // Allow Latin queries (e.g. "xiaomi") and Chinese queries for full list matching.
    if ((!type || type === 'stock') && results.length < 50 && looksLikeWord) {
      const shouldSearchLists = hasHan || lowerQ.length >= 3;
      if (shouldSearchLists) {
        try {
          // Ensure HK list is loaded (this will build index if needed)
          await this.getHkSpotList(config);
          
          // Use index for fast lookup
          if (this.hkNameIndex) {
            // Try exact match first
            let matches = this.hkNameIndex.get(lowerQ) || [];
            
            // If no exact match, try prefix match (for short queries)
            if (matches.length === 0 && lowerQ.length >= 2) {
              matches = this.hkNameIndex.get(lowerQ.substring(0, Math.min(lowerQ.length, 10))) || [];
            }
            
            // If still no match, fallback to substring search in index keys
            // Use uppercase matching to ensure exact character matching (e.g., "BNB" vs "BNK")
            if (matches.length === 0) {
              const upperQ = raw.toUpperCase();
              for (const [key, indexedResults] of this.hkNameIndex.entries()) {
                // Convert key to uppercase for comparison to ensure exact character matching
                const upperKey = key.toUpperCase();
                // Only match if key contains query as substring (not if query contains key)
                // This prevents "xiaomi" from matching "xi" prefix keys
                // Use uppercase to avoid false matches like "bnb" matching "bnk"
                if (upperKey.includes(upperQ) && upperKey.length >= upperQ.length) {
                  matches.push(...indexedResults);
                  if (matches.length >= 50) break;
                }
              }
            }
            
            // Add matches (deduplicated by add function)
            for (const match of matches) {
              if (results.length >= 50) break;
              add(match);
            }
          }
        } catch {
          // ignore search list failure
        }

        try {
          // Ensure A-share list is loaded (this will build index if needed)
          await this.getASpotList(config);
          
          // Use index for fast lookup
          if (this.aNameIndex) {
            // Try exact match first
            let matches = this.aNameIndex.get(lowerQ) || [];
            
            // If no exact match, try prefix match (for short queries)
            if (matches.length === 0 && lowerQ.length >= 2) {
              matches = this.aNameIndex.get(lowerQ.substring(0, Math.min(lowerQ.length, 10))) || [];
            }
            
            // If still no match, fallback to substring search in index keys
            // Use uppercase matching to ensure exact character matching (e.g., "BNB" vs "BNK")
            if (matches.length === 0) {
              const upperQ = raw.toUpperCase();
              for (const [key, indexedResults] of this.aNameIndex.entries()) {
                // Convert key to uppercase for comparison to ensure exact character matching
                const upperKey = key.toUpperCase();
                // Only match if key contains query as substring (not if query contains key)
                // This prevents "xiaomi" from matching "xi" prefix keys
                // Use uppercase to avoid false matches like "bnb" matching "bnk"
                if (upperKey.includes(upperQ) && upperKey.length >= upperQ.length) {
                  matches.push(...indexedResults);
                  if (matches.length >= 50) break;
                }
              }
            }
            
            // Add matches (deduplicated by add function)
            for (const match of matches) {
              if (results.length >= 50) break;
              add(match);
            }
          }
        } catch {
          // ignore search list failure
        }
      }
    }

    // Crypto search is intentionally not implemented here (use CoinGecko/CoinMarketCap, etc.)
    
    // Calculate relevance and filter low-relevance results before returning
    // This prevents false positives like "xiaomi" matching "XI二南三星-U"
    const resultsWithRelevance = results.map(r => {
      const relevance = calculateRelevanceForResult(lowerQ, r.name, r.symbol);
      return { ...r, relevance };
    });
    
    // For short queries (3 characters or less), require stricter matching
    // This prevents "zec" from matching "nzd" or "guanze"
    const isShortQuery = lowerQ.length <= 3;
    const minRelevance = isShortQuery ? 40 : 20; // Require "starts with" or better for short queries
    
    // Filter: only return results with sufficient relevance
    // This filters out false positives from substring matching
    const filteredResults = resultsWithRelevance
      .filter(r => r.relevance >= minRelevance)
      .sort((a, b) => b.relevance - a.relevance); // Sort by relevance descending
    
    return filteredResults.map(({ relevance, ...r }) => r);
  }

  async validateAsset(asset: Partial<Asset>, config?: RequestConfig): Promise<AssetValidationResult> {
    if (!asset.symbol || !asset.type) {
      return { valid: false, error: 'Asset symbol and type are required' };
    }

    const symbol = asset.symbol.toUpperCase();
    const testAsset: Asset = {
      id: asset.id || `${asset.type}-${symbol.toLowerCase().replace(/\./g, '_').replace(/\//g, '_')}`,
      symbol,
      name: asset.name || symbol,
      type: asset.type,
      priceStatus: 'loading',
      displayOrder: 0,
      isActive: true,
      ...(asset.exchange && { exchange: asset.exchange })
    };

    try {
      const priceData = await this.getAssetPrice(testAsset, { ...config, timeout: 15000 });
      const price = priceData.current_price;
      if (!Number.isFinite(price) || price <= 0) {
        return { valid: false, error: `Validation failed: invalid price (${String(price)})` };
      }
      return {
        valid: true,
        asset: {
          ...testAsset,
          price,
          change24h: priceData.price_change_24h,
          changePercent24h: priceData.price_change_percentage_24h,
          lastUpdated: new Date(priceData.last_updated),
          ...(priceData.source_url ? { sourceUrl: priceData.source_url } : {}),
          priceStatus: 'success' as const
        }
      };
    } catch (error) {
      return {
        valid: false,
        error: `Validation failed: ${error instanceof Error ? error.message : String(error)}`
      };
    }
  }

  async testConnection(config?: RequestConfig): Promise<boolean> {
    try {
      await aktoolsService.getVersion({ ...config, timeout: 5000 });
      return true;
    } catch {
      return false;
    }
  }

  private handleError(error: unknown): never {
    // If already an ApiError, rethrow
    if (error instanceof Error && 'code' in error) throw error;

    if (error instanceof Error) {
      // Axios style response
      const anyErr: any = error as any;
      if (anyErr?.response) {
        const response = anyErr.response as any;
        const retryAfter = response.headers ? extractRetryAfter(response.headers as Record<string, string>) : undefined;
        const message = response.data?.error || response.data?.message || error.message;
        throw createApiError(categorizeHttpError(response.status, message), String(message || error.message), {
          provider: this.name,
          originalError: error,
          ...(retryAfter ? { retryAfter } : {})
        });
      }

      if (error.name === 'AbortError' || error.message.toLowerCase().includes('timeout')) {
        throw createApiError('TIMEOUT_ERROR', 'Request timed out', { provider: this.name, originalError: error });
      }

      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw createApiError('NETWORK_ERROR', 'Network connection failed', { provider: this.name, originalError: error });
      }

      throw createApiError('UNKNOWN_ERROR', error.message, { provider: this.name, originalError: error });
    }

    throw createApiError('UNKNOWN_ERROR', 'An unknown error occurred', { provider: this.name, originalError: error });
  }
}

